import React, { useState, useEffect } from 'react';
import { Space, Typography } from 'antd';
import { SaveOutlined, FileTextOutlined } from '@ant-design/icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../hooks/auth';
import Modal from './Modal';
import { DynamicForm } from '../form/DynamicForm';
import { Toast } from '../ui';
import TemplateService from '../../services/templateService';

const { Title, Text } = Typography;

const SaveTemplateModal = ({ 
  open, 
  onCancel, 
  onSave,
  reportBuilderData = {},
  initialTitle = '',
  loading = false 
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ title: '', templateOption: 'new' });
  const [existingTemplates, setExistingTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Reset form quando modal abrir/fechar
  useEffect(() => {
    if (open) {
      const title = initialTitle || reportBuilderData.title || 'Novo Relatório';
      setFormData({ title, templateOption: 'new' });
      loadExistingTemplates();
    } else {
      setFormData({ title: '', templateOption: 'new' });
      setExistingTemplates([]);
    }
  }, [open, initialTitle, reportBuilderData.title]);

  // Carregar modelos existentes
  const loadExistingTemplates = async () => {
    try {
      setLoadingTemplates(true);
      
      const response = await TemplateService.getAll({
        page: 1,
        pageSize: 100,
        tenantId: null // Carregar todos os templates do tenant
      });

      const raw = response?.data?.response;
      const templatesList = Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw) ? raw : []);
      
      // Mapear para o formato esperado pelo dropdown
      const templatesOptions = templatesList.map(template => ({
        id: template.id,
        title: template.title,
        value: template.id,
        label: template.title
      }));

      setExistingTemplates(templatesOptions);
    } catch (error) {
      console.error('Erro ao carregar modelos existentes:', error);
      Toast.error('Erro ao carregar modelos existentes');
      setExistingTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSave = async (values) => {
    try {
      setSaving(true);

      // Verificar se há dados para salvar
      if (!reportBuilderData.widgets || reportBuilderData.widgets.length === 0) {
        Toast.error('Não há widgets para salvar. Adicione pelo menos um gráfico ao relatório.');
        return;
      }

      // Determinar ID do template baseado na opção selecionada
      let templateId = null;
      let finalTitle = values.title.trim();
      
      if (values.templateOption === 'new') {
        // Novo modelo - não passa ID
        templateId = null;
      } else {
        // Sobrescrever modelo existente
        templateId = values.templateOption;
        // Buscar título do modelo existente se não foi alterado
        const existingTemplate = existingTemplates.find(t => t.id === values.templateOption);
        if (existingTemplate && values.title === existingTemplate.title) {
          finalTitle = existingTemplate.title;
        }
      }

      // Preparar dados do template
      const templateData = TemplateService.mapReportBuilderToTemplate({
        ...reportBuilderData,
        title: finalTitle,
        tenantId: user?.tenantId || null,
        id: templateId
      });


      // Chamar service para salvar
      const response = await TemplateService.upsert(templateData);

      if (response.data) {
        const isOverwrite = values.templateOption !== 'new';
        const successMessage = isOverwrite 
          ? 'Modelo sobrescrito com sucesso!' 
          : 'Modelo salvo com sucesso!';
        Toast.success(successMessage);
        
        // Chamar callback do parent
        if (onSave) {
          onSave({
            ...response.data,
            title: finalTitle
          });
        }
        
        // Fechar modal
        onCancel();
      }
    } catch (error) {
      console.error('❌ Erro ao salvar Modelo:', error);
      Toast.error(error.message || 'Erro ao salvar modelo. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({ title: '', templateOption: 'new' });
    setExistingTemplates([]);
    onCancel();
  };

  // Configuração do formulário
  const formConfig = [
    {
      columns: 1,
      questions: [
        {
          id: 'templateOption',
          label: 'Opção de Salvamento',
          type: 'select',
          required: true,
          placeholder: 'Selecione uma opção',
          loading: loadingTemplates,
          options: [
            { value: 'new', label: '📄 Criar novo modelo' },
            ...existingTemplates.map(template => ({
              value: template.id,
              label: `🔄 Sobrescrever: ${template.title}`
            }))
          ],
          onChange: (value) => {
            const selectedTemplate = existingTemplates.find(t => t.id === value);
            if (selectedTemplate && value !== 'new') {
              // Auto-preencher título com o modelo selecionado
              setFormData(prev => ({ ...prev, title: selectedTemplate.title }));
            }
          }
        },
        {
          id: 'title',
          label: 'Título do Modelo',
          type: 'text',
          required: true,
          placeholder: 'Digite um título descritivo para o modelo...',
          prefixIcon: <FileTextOutlined />,
          customValidation: {
            validator: (_, value) => {
              if (!value) return Promise.resolve();
              if (value.length < 3) {
                return Promise.reject(new Error('O título deve ter pelo menos 3 caracteres'));
              }
              if (value.length > 100) {
                return Promise.reject(new Error('O título não pode ter mais de 100 caracteres'));
              }
              return Promise.resolve();
            },
          }
        }
      ]
    }
  ];

      return (
    <Modal
      title={
        <Space>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.primary}AA)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${theme.primary}30`
            }}
          >
            <SaveOutlined style={{ fontSize: 18, color: '#fff' }} />
          </div>
          <div>
            <Title level={4} style={{ margin: 0, color: theme.textSecondary }}>
              Salvar Modelo
            </Title>
            <Text style={{ fontSize: 13, color: theme.textSecondary }}>
              Salve seu relatório como template para reutilizar
            </Text>
          </div>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      variant="form"
      width={520}
      data-tour-id="builder-save-template-modal"
    >
            <div style={{ padding: '20px 8px' }}>
        {/* Informações do Relatório */}
        <div
          style={{
            background: theme.cardBackground || '#fafafa',
            borderRadius: '12px',
            padding: '16px',
            border: `1px solid ${theme.borderColor || '#f0f0f0'}`,
            marginBottom: '20px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <FileTextOutlined style={{ fontSize: 16, color: theme.primary }} />
            <Text strong style={{ color: theme.textSecondary }}>
              Informações do Relatório
            </Text>
          </div>
          
          <div style={{ paddingLeft: 28 }}>
            <div style={{ marginBottom: 8 }}>
              <Text style={{ fontSize: 13, color: theme.textSecondary }}>
                Widgets: {reportBuilderData.widgets?.length || 0}
              </Text>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text style={{ fontSize: 13, color: theme.textSecondary }}>
                Sincronização: {reportBuilderData.sync?.name || 'Não definida'}
              </Text>
            </div>
            <div>
              <Text style={{ fontSize: 13, color: theme.textSecondary }}>
                Período: {reportBuilderData.filters?.start ? 'Definido' : 'Não definido'}
              </Text>
            </div>
          </div>
        </div>

        {/* Formulário com DynamicForm */}
        <DynamicForm
          formConfig={formConfig}
          values={formData}
          setValues={setFormData}
          submitText="Salvar Modelo"
          onSubmit={handleSave}
          onClose={handleCancel}
          loading={saving || loading}
          inputPadding="12px"
          
        />
       </div>
    </Modal>
  );
};

export default SaveTemplateModal;